import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Button,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useStore } from '@mcn-platform/models';
import EditLayout from './EditLayout';
import {
  normalizeForCompare,
  LEGACY_FIELD_BY_TAB_KEY,
  LEGACY_FIELD_DEFAULTS,
} from '../constants/systemPromptDefaults';

const TAB_LABELS: Record<string, string> = {
  shoppingAssistant: 'Shopping Assistant',
  aiSearch: 'AI Search',
  dualSearch: 'Dual Search',
  orderStatus: 'Order Status',
  exitTool: 'Exit Tool',
  productContent: 'Product Content',
  categoryContent: 'Category Content',
  guidesTopics: 'Guides Topics',
  guidesContent: 'Guides Content',
  coreGuideContent: 'Core Guide Content',
  guideCitationInstructions: 'Guide Citation Instructions',
  guideCitationSourcesSuggest: 'Guide Citation Source Suggestions',
  guideEditorialNeutrality: 'Guide Editorial Neutrality',
  guideHumanVoice: 'Guide Human Voice',
  guideDecisionSupport: 'Guide Decision Support',
  guideScoreSystem: 'Guide Score',
  llmsTxt: 'llms.txt Generation',
  askAboutProductWidget: 'Ask About Product Widget',
  portalPromptTemplate: 'Client AI Prompt Template',
};

const TAB_KEYS = Object.keys(TAB_LABELS) as (keyof typeof TAB_LABELS)[];

const emptyRecord = (): Record<string, string> => (
  Object.fromEntries(TAB_KEYS.map((k) => [k, ''])) as Record<string, string>
);

const SystemPrompts = () => {
  const store = useStore();
  const portal = store.currentPortal;
  const [tabIndex, setTabIndex] = useState(0);
  const [legacyValues, setLegacyValues] = useState(emptyRecord);

  useEffect(() => {
    if (!portal?.id || portal.syncState !== 'loaded') return;
    const config = (portal.clientAIConfig ?? {}) as Record<string, unknown>;
    const portalRoot = portal as Record<string, unknown>;
    setLegacyValues({
      ...emptyRecord(),
      ...Object.fromEntries(
        TAB_KEYS.map((k) => {
          const field = LEGACY_FIELD_BY_TAB_KEY[k];
          if (field === 'clientAIPromptTemplate') {
            return [k, (typeof portalRoot.clientAIPromptTemplate === 'string' ? portalRoot.clientAIPromptTemplate : '') as string];
          }
          return [k, (typeof config[field] === 'string' ? config[field] : '') as string];
        }),
      ),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load when portal id/syncState changes
  }, [portal?.id, portal?.syncState]);

  const handleLegacyChange = useCallback((key: string, value: string) => {
    setLegacyValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const savePortal = useCallback(() => {
    if (!portal) return Promise.reject(new Error('No portal'));
    const config = { ...(portal.clientAIConfig ?? {}) } as Record<string, unknown>;
    const portalUpdates: Record<string, string | null> = {};

    TAB_KEYS.forEach((k) => {
      const legacyField = LEGACY_FIELD_BY_TAB_KEY[k];
      const legacyDef = LEGACY_FIELD_DEFAULTS[legacyField] ?? '';
      const legacyNorm = normalizeForCompare(legacyValues[k]) === normalizeForCompare(legacyDef) ? '' : legacyValues[k].trim();
      if (legacyField === 'clientAIPromptTemplate') {
        portalUpdates.clientAIPromptTemplate = legacyNorm || null;
      } else {
        (config as Record<string, string>)[legacyField] = legacyNorm;
      }
    });

    if (Object.keys(portalUpdates).length) {
      portal.updateFields(Object.keys(portalUpdates) as ('clientAIPromptTemplate')[], portalUpdates);
    }
    portal.updateFields(['clientAIConfig'], { clientAIConfig: config });
    return store.update('portal', portal);
  }, [portal, store, legacyValues]);

  const currentConfig = (portal?.clientAIConfig ?? {}) as Record<string, string>;
  const portalRoot = portal as Record<string, string | undefined> | null;
  const legacyDirty = portal && TAB_KEYS.some((k) => {
    const legacyField = LEGACY_FIELD_BY_TAB_KEY[k];
    const legacyDef = LEGACY_FIELD_DEFAULTS[legacyField] ?? '';
    const wouldLegacy = normalizeForCompare(legacyValues[k]) === normalizeForCompare(legacyDef) ? '' : legacyValues[k].trim();
    if (legacyField === 'clientAIPromptTemplate') {
      return (portalRoot?.clientAIPromptTemplate ?? '') !== wouldLegacy;
    }
    return (currentConfig[legacyField] ?? '') !== wouldLegacy;
  });
  const dirty = Boolean(legacyDirty);

  if (!portal) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Select a portal from the sidebar.</Typography>
      </Box>
    );
  }

  if (portal.syncState !== 'loaded') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <EditLayout
      title="System Prompts"
      breadcrumbTitle="System prompts"
      buttonStack={(
        <Button color="primary" variant="contained" onClick={savePortal} disabled={!dirty}>
          Save
        </Button>
      )}
    >
      <Box sx={{ px: 2, pb: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v: number) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          {TAB_KEYS.map((key, i) => (
            <Tab key={key} label={TAB_LABELS[key]} id={`system-prompts-tab-${i}`} />
          ))}
        </Tabs>
        {TAB_KEYS.map((key, i) => (
          <div
            key={key}
            role="tabpanel"
            hidden={tabIndex !== i}
            id={`system-prompts-tabpanel-${i}`}
            aria-labelledby={`system-prompts-tab-${i}`}
          >
            {tabIndex === i && (
              <Box
                sx={{
                  pt: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    minRows={12}
                    maxRows={30}
                    label={`System prompt (override) for ${TAB_LABELS[key]}`}
                    helperText="Uses system default when empty; saving unchanged default stores empty. Additional instructions per flow live with their workflow settings."
                    value={
                      (legacyValues[key] || LEGACY_FIELD_DEFAULTS[LEGACY_FIELD_BY_TAB_KEY[key]])
                      ?? ''
                    }
                    onChange={(e) => handleLegacyChange(key, e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        resize: 'vertical',
                      },
                    }}
                  />
                </Box>
              </Box>
            )}
          </div>
        ))}
      </Box>
    </EditLayout>
  );
};

export default observer(SystemPrompts);
